import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { isAuthenticated, signOut } from '../lib/api';
import { getStorageItem, setStorageItem } from '../utils/storage';
import { error as logError } from '../utils/logger';
import type { Landlord } from '../types/models';
import type { AuthTokens } from '../types/api';

interface AuthContextValue {
  user: Landlord | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (landlordData: Landlord, tokens: AuthTokens) => void;
  logout: () => void;
  updateUser: (landlordData: Landlord) => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<Landlord | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    // Check if user is authenticated on mount
    const checkAuth = async () => {
      try {
        if (isAuthenticated()) {
          // Load user info from localStorage
          const landlordInfo = getStorageItem('landlordInfo');
          if (landlordInfo && isMounted) {
            setUser(landlordInfo as Landlord);
          }
        }
      } catch (error) {
        // Log error (guarded by logger utility)
        logError('Error checking auth:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    checkAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  const login = (landlordData: Landlord, tokens: AuthTokens) => {
    // Store tokens
    setStorageItem('accessToken', tokens.accessToken);
    setStorageItem('idToken', tokens.idToken);
    setStorageItem('refreshToken', tokens.refreshToken);
    setStorageItem('expiresIn', tokens.expiresIn);

    // Store user info
    setStorageItem('landlordId', landlordData.landlord_id);
    setStorageItem('landlordInfo', landlordData);

    setUser(landlordData);
  };

  const updateUser = (landlordData: Landlord) => {
    // Update user info without changing tokens
    // Useful for refreshing user data after updates (e.g., preferences)
    setStorageItem('landlordInfo', landlordData);
    setUser(landlordData);
  };

  const logout = () => {
    signOut();
    setUser(null);
  };

  // Compute isAuthenticated based on user state and token validity
  // This ensures it's reactive to changes in authentication state
  const authenticated = useMemo(() => {
    // If user is set, we're authenticated
    if (user) return true;
    // Otherwise check token validity
    return isAuthenticated();
  }, [user]);

  const value = useMemo(() => ({
    user,
    loading,
    isAuthenticated: authenticated,
    login,
    logout,
    updateUser,
  }), [user, loading, authenticated]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
